import { useState, useEffect, useMemo } from "react";
import {
  IconPencil,
  IconPlus,
  IconSearch,
  IconSpinner,
  IconTrash,
} from "../icons";
import { INSURERS } from "../../../config/insurers";
import { notifyError, notifySuccess } from "../notify";
import { useAdminModal } from "../components/AdminModalProvider";
import { useNavigate, useOutletContext } from "react-router-dom";

const getSchemePrefix = (key) => {
  if (!key) return null;
  const match = String(key).match(/^(\d+)-/);
  return match ? match[1] : null;
};

const matchesSchemeFilter = (key, schemeFilter) => {
  if (!schemeFilter || schemeFilter === "all") return true;
  const prefix = getSchemePrefix(key);
  return prefix === schemeFilter;
};

const getSchemeFilterLabel = (prefix) => {
  if (!prefix) return "Unknown scheme";

  const insurer = INSURERS.find((entry) =>
    entry.schemeTableIds.includes(Number(prefix)),
  );

  return insurer ? `${insurer.key} (${prefix})` : `Scheme ${prefix}`;
};

const matchesSearch = (feature, query) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  const haystack = [
    feature.featureName,
    feature.schemeFeatureDescriptionKey,
    feature.category,
    feature.schemeFeatureDefinition,
  ]
    .filter(Boolean)
    .map((value) => String(value).toLowerCase());

  if (haystack.some((value) => value.includes(q))) return true;

  const descriptions = feature.schemeDescriptions || {};
  return Object.entries(descriptions).some(([key, lines]) => {
    if (key.toLowerCase().includes(q)) return true;
    return (
      Array.isArray(lines) &&
      lines.some((line) =>
        String(line)
          .toLowerCase()
          .includes(q),
      )
    );
  });
};

export default function SchemeFeatureManagement() {
  const [schemeFeatures, setSchemeFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [schemeFilter, setSchemeFilter] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();
  const { confirm } = useAdminModal();
  const { setPageSubtitle } = useOutletContext() || {};

  const schemePrefixes = useMemo(() => {
    const prefixes = new Set();
    schemeFeatures.forEach((feature) => {
      const prefix = getSchemePrefix(feature.schemeFeatureDescriptionKey);
      if (prefix) prefixes.add(prefix);
    });
    return Array.from(prefixes).sort((a, b) => Number(a) - Number(b));
  }, [schemeFeatures]);

  const filteredSchemeFeatures = useMemo(
    () =>
      schemeFeatures.filter(
        (feature) =>
          matchesSchemeFilter(
            feature.schemeFeatureDescriptionKey,
            schemeFilter,
          ) && matchesSearch(feature, searchTerm),
      ),
    [schemeFeatures, schemeFilter, searchTerm],
  );

  useEffect(() => {
    fetchSchemeFeatures();
  }, []);

  useEffect(() => {
    if (!setPageSubtitle) return undefined;

    const parts = [
      `Showing ${filteredSchemeFeatures.length} of ${schemeFeatures.length} features`,
    ];
    if (schemeFilter !== "all") {
      parts.push(`scheme ${getSchemeFilterLabel(schemeFilter)}`);
    }
    if (searchTerm.trim()) {
      parts.push(`matching "${searchTerm.trim()}"`);
    }

    setPageSubtitle(parts.join(" · "));

    return () => setPageSubtitle(null);
  }, [
    filteredSchemeFeatures.length,
    schemeFeatures.length,
    schemeFilter,
    searchTerm,
    setPageSubtitle,
  ]);

  const fetchSchemeFeatures = async () => {
    try {
      const response = await fetch("/api/policies/all", {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "application/json",
        },
      });
      if (!response.ok) {
        throw new Error("Failed to fetch scheme features");
      }
      const data = await response.json();
      setSchemeFeatures(data.AllSchemeFeatureDescriptions || []);
    } catch (error) {
      notifyError("Error loading scheme features");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    const confirmed = await confirm({
      title: "Delete scheme feature?",
      description:
        "Are you sure you want to delete this scheme feature? This will also delete all related descriptions and panel items.",
      confirmLabel: "Delete",
      cancelLabel: "Cancel",
      tone: "danger",
    });

    if (!confirmed) return;

    setActionLoading(true);
    try {
      const descriptionsResponse = await fetch(
        `/api/scheme-features/delete-descriptions/panel-members/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
            "Content-Type": "application/json",
          },
        },
      );

      if (!descriptionsResponse.ok) {
        throw new Error("Failed to delete scheme feature");
      }

      await fetchSchemeFeatures();
      notifySuccess("Scheme feature and all related data deleted successfully");
    } catch (error) {
      notifyError(
        error.message === "Failed to fetch"
          ? "Network error. Please check your connection."
          : error.message,
      );
    } finally {
      setActionLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <IconSpinner size={32} />
        <p>Loading scheme features...</p>
      </div>
    );
  }

  return (
    <div className="scheme-feature-management">
      <div
        className="scheme-feature-toolbar"
        role="toolbar"
        aria-label="Scheme features filters"
      >
        <label className="scheme-feature-toolbar__search">
          <IconSearch
            size={16}
            className="scheme-feature-toolbar__search-icon"
            aria-hidden
          />
          <input
            type="search"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            placeholder="Search name, key, category, descriptions…"
            aria-label="Search scheme features"
          />
        </label>

        <select
          id="scheme-filter"
          className="scheme-feature-toolbar__scheme"
          value={schemeFilter}
          onChange={(e) => setSchemeFilter(e.target.value)}
          aria-label="Filter by scheme"
        >
          <option value="all">All schemes</option>
          {schemePrefixes.map((prefix) => (
            <option key={prefix} value={prefix}>
              {getSchemeFilterLabel(prefix)}
            </option>
          ))}
        </select>

        <button
          type="button"
          className="scheme-feature-toolbar__add"
          onClick={() => navigate("/admin/dashboard/scheme-features/new")}
          disabled={actionLoading}
        >
          <IconPlus size={16} />
          Add New
        </button>
      </div>

      <div className="management-grid">
        {filteredSchemeFeatures.length === 0 ? (
          <div className="no-data-message scheme-feature-toolbar__empty">
            {searchTerm.trim()
              ? `No features match "${searchTerm.trim()}".`
              : schemeFilter === "all"
              ? "No scheme features found."
              : `No features found for scheme ${schemeFilter}.`}
          </div>
        ) : (
          filteredSchemeFeatures.map((feature) => (
            <article
              key={feature.id ?? feature.schemeFeatureDescriptionKey}
              className="management-card scheme-feature-card"
            >
              <div className="scheme-feature-card__body">
                <header className="scheme-feature-card__header">
                  <div className="scheme-feature-card__title-wrap">
                    <h3 className="scheme-feature-card__title">
                      {feature.featureName || "Unnamed Feature"}
                    </h3>
                  </div>
                  <div className="scheme-feature-card__actions">
                    <button
                      type="button"
                      className="scheme-feature-card__action-btn edit-btn"
                      onClick={() =>
                        navigate(
                          `/admin/dashboard/scheme-features/edit/${feature.id}`,
                        )
                      }
                      disabled={actionLoading}
                      title="Edit scheme feature"
                      aria-label="Edit scheme feature"
                    >
                      <IconPencil size={18} />
                    </button>
                    <button
                      type="button"
                      className="scheme-feature-card__action-btn delete-btn"
                      onClick={() => handleDelete(feature.id)}
                      disabled={actionLoading}
                      title="Delete scheme feature"
                      aria-label="Delete scheme feature"
                    >
                      <IconTrash size={18} />
                    </button>
                  </div>
                </header>

                <div className="scheme-feature-card__content">
                  {feature.schemeFeatureDefinition ? (
                    <p className="scheme-feature-card__definition">
                      {feature.schemeFeatureDefinition}
                    </p>
                  ) : null}

                  {Object.entries(feature.schemeDescriptions || {}).map(
                    ([sectionKey, descriptions]) => (
                      <div
                        key={sectionKey}
                        className="scheme-feature-card__section"
                      >
                        <p className="scheme-feature-card__section-label">
                          {sectionKey}
                        </p>
                        <ul className="scheme-feature-card__section-list">
                          {descriptions.map((desc, index) => (
                            <li
                              key={index}
                              className="scheme-feature-card__section-text"
                            >
                              {desc}
                            </li>
                          ))}
                        </ul>
                      </div>
                    ),
                  )}

                  {feature.panelMemberPanelItem ? (
                    <div className="scheme-feature-card__section">
                      <p className="scheme-feature-card__section-label">
                        Panel item
                      </p>
                      <div className="scheme-feature-card__panel-lines">
                        {feature.panelMemberPanelItem.PanelMemberPanelItemLines?.map(
                          (line, index) => (
                            <div
                              key={index}
                              className="scheme-feature-card__panel-line"
                            >
                              <span className="scheme-feature-card__section-text">
                                {line.PanelItemLine}
                              </span>
                              {line.PanelItemLineFormat ? (
                                <small className="scheme-feature-card__panel-meta">
                                  ({line.PanelItemLineFormat})
                                </small>
                              ) : null}
                            </div>
                          ),
                        )}
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              <footer className="scheme-feature-card__footer">
                <span className="scheme-feature-card__key">
                  {feature.schemeFeatureDescriptionKey}
                </span>
                {feature.category ? (
                  <span className="scheme-feature-card__badge">
                    {feature.category}
                  </span>
                ) : null}
              </footer>
            </article>
          ))
        )}
      </div>
    </div>
  );
}
